"use client";

import { LoaderCircle, Timer } from "lucide-react";
import Image from "next/image";
import { QRCodeSVG } from "qrcode.react";
import { useEffect, useState } from "react";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer";
import { formatCountdown, formatPaymentAmountWhole } from "../lib/format";
import type { RazorpayCustomPaymentController } from "./use-razorpay-custom-payment";

/**
 * Seconds left, recomputed from the provider's own `close_by`.
 *
 * Counting down a local number would drift against the code Razorpay will
 * actually stop honouring, and a backgrounded phone — the normal case here,
 * since the payer has just switched to their UPI app — would freeze the timer
 * entirely and then claim minutes of validity the QR no longer has.
 */
const useSecondsRemaining = (expiresAtIso: string | null) => {
  const [remaining, setRemaining] = useState(() =>
    expiresAtIso
      ? Math.max(0, Math.round((Date.parse(expiresAtIso) - Date.now()) / 1000))
      : 0,
  );

  useEffect(() => {
    if (!expiresAtIso) return;
    const expiresAt = Date.parse(expiresAtIso);
    const tick = () =>
      setRemaining(Math.max(0, Math.round((expiresAt - Date.now()) / 1000)));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [expiresAtIso]);

  return remaining;
};

/**
 * The screen a payer waits on while a UPI app is being opened.
 *
 * ## Why a screen, and not a spinner on the Pay button
 *
 * Because the handoff cannot report itself, and the previous build let the
 * provider's guess about it end the payment. Razorpay's SDK fires the deep
 * link as a top-level navigation and then decides "no app" from the payer's
 * next touch on a document that never went hidden, so `intent_no_apps_error`
 * arrives identically for an app that is missing, an "Open in PhonePe?" prompt
 * the payer declined, and a scheme Safari refused because the navigation had
 * lost its user activation. Acting on that verdict is what hid a working Google
 * Pay for thirty days.
 *
 * So the verdict lands here as one line of advice, and the screen keeps the one
 * control that actually recovers: **Open App** re-fires the link from inside a
 * real tap. The automatic attempt cannot — by the time it runs, the order has
 * been prepared and the customer looked up, and two awaited round trips is
 * exactly how a gesture is lost.
 *
 * Nothing here is allowed to imply we know what is installed, and nothing here
 * removes a method from the payer's list.
 */
function UpiIntentHandoffView({
  controller,
}: {
  controller: RazorpayCustomPaymentController;
}) {
  const handoff = controller.upiIntentHandoff;
  if (!handoff) return null;

  return (
    <div className="overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+20px)]">
      <div className="flex flex-col items-center pt-6 text-center">
        {/*
          The app's own mark where we have one, and the spinner around it, so the
          screen says which app it is waiting for without a line of prose.
        */}
        <div className="relative flex h-16 w-16 items-center justify-center">
          <LoaderCircle
            className="absolute inset-0 h-16 w-16 animate-spin text-brand dark:text-white/70"
            strokeWidth={2}
            aria-hidden="true"
          />
          {handoff.iconUrl ? (
            <span className="flex h-9 w-9 items-center justify-center overflow-hidden rounded-lg bg-white">
              <Image
                src={handoff.iconUrl}
                alt=""
                width={28}
                height={28}
                className="h-7 w-7 object-contain"
                unoptimized
                loading="eager"
              />
            </span>
          ) : null}
        </div>

        <h3 className="mt-5 text-xl font-semibold tracking-tight">
          Complete payment in {handoff.appName}
        </h3>

        <p className="mt-2 text-sm text-text-secondary dark:text-white/62">
          {formatPaymentAmountWhole(handoff.amountMinor, handoff.currency)} to{" "}
          {handoff.merchantName}
        </p>

        {/*
          Only after the handoff has visibly not happened. Shown any earlier it
          would be a guess about the payer's phone, which is the mistake this
          whole screen exists to stop making — and the wording keeps the two
          real causes side by side, because the provider cannot tell them apart.
        */}
        {handoff.appOpenFailed ? (
          <p className="mt-4 text-sm text-text-secondary dark:text-white/70">
            Unable to open {handoff.appName}. Tap Open App to try again, or use
            another payment method.
          </p>
        ) : null}

        <div className="mt-5 flex items-center justify-center gap-2 rounded-full bg-surface-alt px-3 py-2 text-xs text-text-secondary dark:bg-glass-bg-04 dark:text-white/68">
          <span
            className="h-1.5 w-1.5 rounded-full bg-brand dark:bg-white/70"
            aria-hidden="true"
          />
          <span>Waiting for confirmation…</span>
        </div>

        <button
          type="button"
          onClick={controller.retryUpiIntentApp}
          disabled={handoff.retrying}
          className="mt-5 flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-brand text-base font-semibold text-brand-foreground transition-colors hover:bg-brand-hover disabled:opacity-60 dark:bg-white dark:text-black dark:hover:bg-white/90"
        >
          {handoff.retrying ? (
            <LoaderCircle className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : null}
          <span>Open App</span>
        </button>

        <button
          type="button"
          onClick={controller.cancelUpiIntentHandoff}
          className="mt-2.5 h-12 w-full rounded-xl border border-border text-base font-semibold text-text-secondary dark:border-white/[0.14] dark:text-white/78"
        >
          Cancel
        </button>

        {/*
          Said out loud because this screen cannot prove a negative. If the app
          did open and the payment went through, the webhook settles the booking
          whatever happens here — and the poll behind this drawer is watching for
          exactly that.
        */}
        <p className="mt-4 text-[11px] text-text-tertiary dark:text-white/40">
          Already paid? Closing this won't cancel it — we'll confirm as soon as
          the payment lands.
        </p>
      </div>
    </div>
  );
}

/**
 * The QR screen.
 *
 * The UPI app tiles used to live here too, which was the wrong place: choosing
 * an app is a *method choice*, so it belongs in the payment-method sheet beside
 * the cards and the saved UPI IDs. Sending somebody to a second surface to pick
 * which UPI, after they had already picked UPI, was a step that bought nothing.
 *
 * A code is different — it has to be looked at, and it has to be able to expire
 * while somebody looks at it — so it keeps a screen of its own.
 */
function UpiQrView({
  controller,
}: {
  controller: RazorpayCustomPaymentController;
}) {
  const session = controller.upiSession;
  const remaining = useSecondsRemaining(session?.expiresAtIso ?? null);
  const isExpired = Boolean(session) && remaining <= 0;
  if (!session) return null;

  return (
    <div className="overflow-y-auto px-4 pb-[calc(env(safe-area-inset-bottom)+20px)]">
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold tracking-tight">
          {formatPaymentAmountWhole(session.amountMinor, session.currency)}
        </span>
        <span className="text-xs text-text-tertiary dark:text-white/45">
          to {session.merchantName}
        </span>
      </div>

      <p className="mt-4 text-center text-xs text-text-tertiary dark:text-white/45">
        Scan with any UPI app — Google Pay, PhonePe, Paytm, CRED or your bank's
        app
      </p>

      {/*
        White plate, always. A QR is read as dark-on-light by every scanner
        worth supporting, and inheriting this app's dark surface would hand
        the payer a code their camera cannot resolve.
      */}
      <div className="mt-2 flex justify-center">
        <div className="relative rounded-2xl bg-white p-4">
          <QRCodeSVG
            value={session.upiUri}
            size={216}
            level="M"
            marginSize={0}
            bgColor="#ffffff"
            fgColor="#000000"
          />
          {isExpired ? (
            <div className="absolute inset-0 flex items-center justify-center rounded-2xl bg-white/92 px-4 text-center text-sm font-semibold text-slate-700">
              This code has expired
            </div>
          ) : null}
        </div>
      </div>

      <div className="mt-3 flex items-center justify-center gap-2 text-xs text-text-secondary dark:text-white/62">
        {isExpired ? (
          <span>Close this and tap Pay again for a fresh code.</span>
        ) : (
          <>
            <Timer className="h-3.5 w-3.5" aria-hidden="true" />
            <span>{formatCountdown(remaining)} remaining</span>
          </>
        )}
      </div>

      <div className="mt-4 flex items-center justify-center gap-2 rounded-xl bg-surface-alt px-3 py-2.5 text-xs text-text-secondary dark:bg-glass-bg-04 dark:text-white/68">
        <LoaderCircle className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
        <span>
          Waiting for payment. This confirms on its own — keep this open.
        </span>
      </div>

      <button
        type="button"
        onClick={controller.cancelUpiSession}
        className="mt-3 h-11 w-full rounded-xl border border-border text-sm font-semibold text-text-secondary dark:border-white/[0.14] dark:text-white/78"
      >
        Cancel
      </button>

      {/*
        Said out loud because the drawer cannot prove a negative. If the
        payer approved in their app and then closed this, the webhook still
        settles the booking — and the payment page they land back on polls
        for exactly that.
      */}
      <p className="mt-3 text-center text-[11px] text-text-tertiary dark:text-white/40">
        Already paid? Closing this won't cancel it — we'll confirm your booking
        as soon as the payment lands.
      </p>
    </div>
  );
}

/**
 * One drawer for a UPI payment already in flight, in either of its two shapes.
 *
 * They are mutually exclusive by construction — `launchPayment` takes one
 * branch or the other — and they share the rule that matters: while either is
 * on screen the launch promise stays unresolved, so the caller cannot re-enable
 * Pay underneath a payment that may be completing in another app.
 */
export function UpiPaymentDrawer({
  controller,
}: {
  controller: RazorpayCustomPaymentController;
}) {
  const handoff = controller.upiIntentHandoff;
  const session = controller.upiSession;
  const open = Boolean(handoff ?? session);

  return (
    <Drawer
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        if (handoff) {
          controller.cancelUpiIntentHandoff();
          return;
        }
        controller.cancelUpiSession();
      }}
      repositionInputs={false}
    >
      <DrawerContent className="mx-auto max-h-[92dvh] max-w-[520px] rounded-t-[24px] border-border bg-surface text-text-primary shadow-xl dark:border-white/[0.12] dark:bg-[#111114] dark:text-white">
        <DrawerHeader className="px-4 pb-1 text-left">
          <DrawerTitle
            className={handoff ? "sr-only" : "text-xl font-semibold"}
          >
            {handoff ? "Complete payment in your UPI app" : "Scan to pay"}
          </DrawerTitle>
        </DrawerHeader>

        {handoff ? (
          <UpiIntentHandoffView controller={controller} />
        ) : (
          <UpiQrView controller={controller} />
        )}
      </DrawerContent>
    </Drawer>
  );
}
